/**
 * Landing page for the "Confirmer mon email" link sent after registration.
 * Reads the ?token=... query param, calls /api/auth/verify-email, then either
 * auto-logs the user in (redirect → /dashboard) or shows an error with a
 * "Renvoyer" fallback.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '../context/AuthContext';

export default function AuthVerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { verifyEmail } = useAuth();

  // 3 states: 'loading' | 'success' | 'error'
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage(t('auth.verifyMissingToken', { defaultValue: 'Lien invalide (token manquant).' }));
      return;
    }
    (async () => {
      try {
        await verifyEmail(token);
        setStatus('success');
        // Small delay so the user sees the success animation
        setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
      } catch (e) {
        setStatus('error');
        setMessage(e?.response?.data?.detail || t('auth.verifyGenericError', { defaultValue: 'Impossible de valider ce lien. Il est peut-être expiré.' }));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7] px-6 py-12" data-testid="verify-email-page">
      <div className="max-w-md w-full bg-white border border-[#E2E2DF] rounded-lg shadow-sm p-8 text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-10 h-10 text-[#1A3C34] animate-spin mx-auto mb-4" />
            <h1 className="font-heading text-xl font-semibold text-[#1A3C34]">
              {t('auth.verifyLoading', { defaultValue: 'Validation en cours…' })}
            </h1>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle className="w-14 h-14 text-green-600 mx-auto mb-4" data-testid="verify-success" />
            <h1 className="font-heading text-xl font-semibold text-[#1A3C34] mb-2">
              {t('auth.verifySuccessTitle', { defaultValue: 'Email confirmé !' })}
            </h1>
            <p className="text-sm text-[#737373]">
              {t('auth.verifySuccessSubtitle', { defaultValue: 'Vous êtes maintenant connecté. Redirection vers votre tableau de bord…' })}
            </p>
          </>
        )}
        {status === 'error' && (
          <>
            <AlertCircle className="w-14 h-14 text-red-500 mx-auto mb-4" data-testid="verify-error" />
            <h1 className="font-heading text-xl font-semibold text-[#1A3C34] mb-2">
              {t('auth.verifyErrorTitle', { defaultValue: 'Validation impossible' })}
            </h1>
            <p className="text-sm text-red-600 mb-4">{message}</p>
            <Link to="/auth">
              <Button variant="outline" className="w-full border-[#1A3C34] text-[#1A3C34]" data-testid="verify-back-to-auth">
                {t('auth.verifyBackToAuth', { defaultValue: 'Retour à la page de connexion' })}
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
